import { __assign, __rest } from "tslib";
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { EditorContent, useEditor } from '@tiptap/react';
import Bold from '@tiptap/extension-bold';
import Link from '@tiptap/extension-link';
import BlockQuote from '@tiptap/extension-blockquote';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import StrikeThrough from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import UnorderedList from '@tiptap/extension-bullet-list';
import uniqueId from '../utilities/uniqueId';
import './RichTextField.scss';
import RichTextFieldIcon from './RichTextFieldIcon';
import Modal from './Modal';
import ButtonSecondary from './ButtonSecondary';
import TextInput from './TextInput';
import { URL_REGEX } from '../utilities/urlUtils';
import useToggle from '../hooks/useToggle';
import ButtonPrimary from './ButtonPrimary';
var handleClickOnBlockQuote = function (editor) {
    /**
     * TipTap allows us to change a blockquote to an (un)ordered list but DOES NOT
     * handle changing an (un)ordered list into a blockquote.  The below checks will
     * perform a chain of operations to fix this UX:
     *    1 - remove any nodes (blockquote, (un)ordered lists)
     *    2 - apply blockquote
     *    3 - apply desired list style
     *
     * This logic is effectively doubled because we need to handle the reverse flow as well
     * to remove blockquote styling if the element is a blockquoted (un)ordered list
     */
    if (editor.isActive('bulletList')) {
        if (editor.isActive('blockquote')) {
            return editor.chain().focus().clearNodes().toggleBulletList().run();
        }
        else {
            return editor
                .chain()
                .focus()
                .clearNodes()
                .toggleBlockquote()
                .toggleBulletList()
                .run();
        }
    }
    else if (editor.isActive('orderedList')) {
        if (editor.isActive('blockquote')) {
            return editor.chain().focus().clearNodes().toggleOrderedList().run();
        }
        else {
            return editor
                .chain()
                .focus()
                .clearNodes()
                .toggleBlockquote()
                .toggleOrderedList()
                .run();
        }
    }
    else {
        return editor.chain().focus().toggleBlockquote().run();
    }
};
/**
 * For some reason th default keyboard shortcut of Mod-Shift-B was being intercepted by
 * the keyboard shortcut for applying Bold formatting.  To solve this we use the alt key
 * instead of Mod (cmd on Mac & ctrl on Windows)
 */
var CustomBlockQuote = BlockQuote.extend({
    addKeyboardShortcuts: function () {
        var _this = this;
        return {
            'Alt-Shift-b': function () { return handleClickOnBlockQuote(_this.editor); }
        };
    }
});
var isValidLink = function (link) { return URL_REGEX.test(link); };
var LinkInputModal = function (_a) {
    var onClose = _a.onClose, onConfirm = _a.onConfirm;
    var _b = useState(''), link = _b[0], setLink = _b[1];
    var _c = useState(null), error = _c[0], setError = _c[1];
    var handleConfirm = function () {
        if (!link || !isValidLink(link)) {
            setError('Please enter valid link');
            return;
        }
        setError(null);
        onConfirm(link);
        onClose();
    };
    return (React.createElement(Modal, { isOpen: true, onClose: onClose, size: 'small', title: 'Insert Link' },
        React.createElement(Modal.Body, null,
            React.createElement(TextInput, { errorMessage: isValidLink(link) ? null : error, inputWidth: 'full', label: 'Url', onChange: function (value) { return setLink(String(value)); }, placeholder: 'Enter your link here', value: link })),
        React.createElement(Modal.Footer, null,
            React.createElement(ButtonPrimary, { label: 'Insert', onClick: handleConfirm }),
            React.createElement(ButtonSecondary, { label: 'Cancel', onClick: onClose }))));
};
/**
 * Additional div elements are used to group individual icon buttons within
 * the MenuBar.  We do this so they reflow in a predictable way when the
 * RichTextField element is used in places with different available widths
 */
var MenuBar = function (_a) {
    var editor = _a.editor, 
    // Hyperlink functionality is not needed at the moment.
    _handleToggleLink = _a.handleToggleLink, isEditorDisabled = _a.isEditorDisabled;
    return (React.createElement("div", { className: 'RichTextField__menuBar' },
        React.createElement("div", null,
            React.createElement(RichTextFieldIcon, { isActive: editor.isActive('bold'), isDisabled: isEditorDisabled, onClick: function () { return editor.chain().focus().toggleBold().run(); }, type: 'bold' }),
            React.createElement(RichTextFieldIcon, { isActive: editor.isActive('italic'), isDisabled: isEditorDisabled, onClick: function () { return editor.chain().focus().toggleItalic().run(); }, type: 'italics' }),
            React.createElement(RichTextFieldIcon, { isActive: editor.isActive('underline'), isDisabled: isEditorDisabled, onClick: function () { return editor.chain().focus().toggleUnderline().run(); }, type: 'underline' }),
            React.createElement(RichTextFieldIcon, { isActive: editor.isActive('strike'), isDisabled: isEditorDisabled, onClick: function () { return editor.chain().focus().toggleStrike().run(); }, type: 'strikethrough' })),
        React.createElement("div", null,
            React.createElement(RichTextFieldIcon, { isActive: editor.isActive('blockquote'), isDisabled: isEditorDisabled, onClick: function () { return handleClickOnBlockQuote(editor); }, type: 'blockquote' }),
            React.createElement(RichTextFieldIcon, { isActive: editor.isActive('bulletList'), isDisabled: isEditorDisabled, onClick: function () { return editor.chain().focus().toggleBulletList().run(); }, type: 'unorderedList' }),
            React.createElement(RichTextFieldIcon, { isActive: editor.isActive('orderedList'), isDisabled: isEditorDisabled, onClick: function () { return editor.chain().focus().toggleOrderedList().run(); }, type: 'orderedList' }),
            React.createElement(RichTextFieldIcon, { isDisabled: isEditorDisabled, onClick: function () {
                    return editor.chain().focus().unsetAllMarks().clearNodes().run();
                }, type: 'clearFormatting' })),
        React.createElement("div", null,
            React.createElement(RichTextFieldIcon, { isDisabled: !editor.can().undo() || isEditorDisabled, onClick: function () { return editor.chain().focus().undo().run(); }, type: 'undo' }),
            React.createElement(RichTextFieldIcon, { isDisabled: !editor.can().redo() || isEditorDisabled, onClick: function () { return editor.chain().focus().redo().run(); }, type: 'redo' }))));
};
var RichTextField = function (_a) {
    var className = _a.className, editorContentLastSynched = _a.editorContentLastSynched, errorMessage = _a.errorMessage, isDisabled = _a.isDisabled, label = _a.label, onBlur = _a.onBlur, onChange = _a.onChange, placeholder = _a.placeholder, shouldAllowFullHeight = _a.shouldAllowFullHeight, value = _a.value, props = __rest(_a, ["className", "editorContentLastSynched", "errorMessage", "isDisabled", "label", "onBlur", "onChange", "placeholder", "shouldAllowFullHeight", "value"]);
    /**
     * For some reason simply using uniqueId results in the editor and label having
     * different IDs.  Using a ref here ensures both elements have the same ID
     */
    var editorId = useRef(uniqueId('RichTextFieldEditor'));
    var _b = useToggle(false), isModalOpen = _b[0], openModal = _b[1], closeModal = _b[2];
    var handleToggleLink = function (editor) {
        if (!editor) {
            return;
        }
        openModal();
    };
    /**
     * Check out TipTap's docs for more information about TipTap & how to use it with React!
     *
     * https://tiptap.dev/installation/react
     */
    var tipTapEditor = useEditor({
        editable: !isDisabled,
        editorProps: {
            attributes: {
                'aria-labelledby': editorId.current
            }
        },
        extensions: [
            CustomBlockQuote,
            Bold,
            Document,
            History,
            Italic,
            Link.extend({
                inclusive: false
            }),
            ListItem,
            OrderedList,
            Paragraph,
            Placeholder.configure({
                placeholder: placeholder
            }),
            StrikeThrough,
            Text,
            Underline,
            UnorderedList,
        ],
        content: value,
        onUpdate: function (_a) {
            var editor = _a.editor;
            // this will return empty string instead of empty <p> element when user clears the text or adds empty blank spaces
            onChange(editor.getText().trim().length ? editor.getHTML() : '');
        },
        onBlur: onBlur
    });
    useEffect(function () {
        tipTapEditor === null || tipTapEditor === void 0 ? void 0 : tipTapEditor.commands.setContent(value !== null && value !== void 0 ? value : '');
        /**
         * Only force-update the editor's content when a new datetime value is provided
         * to editorContentLastSynched.  This prop is used exclusively for managing forced updates
         * when RTF content is asynchronously fetched or needs to be updated by a parent component
         * after the RTF is mounted.  The missing dep is `value` and we DO NOT want to force-update
         * the RTF whenever it changes
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorContentLastSynched, tipTapEditor]);
    /**
     * TipTap doesn't handle asynchronous loading of an initial value gracefully.  Furthermore
     * leveraging the dependency array as the second argument of useEditor results in focus
     * being removed from the editor and ruining the UX.  To circumvent needing to conditionally
     * render the RTF component everywhere an initial value is loaded asynchronously the below
     * useState & useEffect will force the content to update exactly once when the value
     * prop has finally loaded successfully.
     */
    var _c = useState(false), asyncInitialValueHasBeenSet = _c[0], setAsyncInitialValueHasBeenSet = _c[1];
    useEffect(function () {
        if (value && !asyncInitialValueHasBeenSet) {
            setAsyncInitialValueHasBeenSet(true);
            tipTapEditor === null || tipTapEditor === void 0 ? void 0 : tipTapEditor.commands.setContent(value);
        }
    }, [asyncInitialValueHasBeenSet, tipTapEditor, value]);
    var rootDivClassNames = classnames('RichTextField', { 'RichTextField--fullHeight': shouldAllowFullHeight }, className);
    var richTextInputContainerClassNames = classnames('RichTextField__inputContainer', { 'RichTextField__inputContainer--fullHeight': shouldAllowFullHeight }, { 'RichTextField__inputContainer--isInvalid': errorMessage });
    var editorClassNames = classnames('RichTextField__editor', { 'RichTextField__editor--fullHeight': shouldAllowFullHeight }, {
        'RichTextField__editor--isDisabled': isDisabled
    });
    return tipTapEditor ? (React.createElement("div", __assign({ className: rootDivClassNames }, props),
        label ? (React.createElement("label", { className: 'TextInput__label', id: editorId.current }, label)) : null,
        React.createElement("div", { className: richTextInputContainerClassNames },
            React.createElement(EditorContent, { className: editorClassNames, editor: tipTapEditor }),
            React.createElement("hr", { className: 'RichTextField__divider' }),
            React.createElement(MenuBar, { editor: tipTapEditor, handleToggleLink: handleToggleLink, isEditorDisabled: isDisabled }),
            isModalOpen ? (React.createElement(LinkInputModal, { onClose: closeModal, onConfirm: function (url) {
                    tipTapEditor.chain().focus().setLink({ href: url }).run();
                } })) : null),
        errorMessage ? (React.createElement("div", { className: 'RichTextField__errorMessage', role: 'alert' }, errorMessage)) : null)) : null;
};
RichTextField.defaultProps = {
    onBlur: function () { }
};
RichTextField.propTypes = {
    className: PropTypes.string,
    editorContentLastSynched: PropTypes.number,
    errorMessage: PropTypes.string,
    isDisabled: PropTypes.bool,
    label: PropTypes.node,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    shouldAllowFullHeight: PropTypes.bool,
    value: PropTypes.string
};
export default RichTextField;
//# sourceMappingURL=RichTextField.js.map